import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuthContext } from './context/AuthContext'; // 1. Importar o AuthContext
import axios from 'axios'; // 2. Importar axios

const EntidadeContext = createContext(null);

export const EntidadeProvider = ({ children }) => {
  const { authState, loggedInUser } = useAuthContext();
  const [currentEntidade, _setCurrentEntidade] = useState(null); // Inicializa como null
  const [isLoadingEntidade, setIsLoadingEntidade] = useState(true);

  // --- DADOS DE CONFIGURAÇÃO DA AGENDA ---
  const [agendaConfig, setAgendaConfig] = useState({ feriados: [], diasBloqueados: [] });
  const [isLoadingAgendaConfig, setIsLoadingAgendaConfig] = useState(true);

  // --- NOVOS ESTADOS PARA DADOS DE SUPORTE DA ENTIDADE ---
  const [supportData, setSupportData] = useState({
    especialidades: [],
    todosMedicos: [],
    allFuncionarios: [],
    pacientesList: [],
    servicos: [],
  });
  const [isLoadingSupportData, setIsLoadingSupportData] = useState(true);

  const setCurrentEntidade = useCallback((entidade) => {
    _setCurrentEntidade(entidade);
    // Otimização: Se a entidade atualizada já contém os dados da agenda,
    // atualizamos o estado de agendaConfig diretamente, evitando um re-fetch.
    if (entidade && (entidade.feriados || entidade.diasBloqueados)) {
      setAgendaConfig({
        feriados: entidade.feriados || [],
        diasBloqueados: entidade.diasBloqueados || []
      });
    }
  }, []);

  // Função para adicionar um novo paciente à lista de suporte de dados
  const addPacienteToList = useCallback((newPaciente) => {
    setSupportData(prevSupportData => ({
      ...prevSupportData,
      pacientesList: [...prevSupportData.pacientesList, newPaciente].sort((a, b) => a.nome.localeCompare(b.nome))
    }));
  }, []);

  // Efeito para inicializar a entidade a partir do estado de autenticação
  useEffect(() => {
    if (loggedInUser && authState.entidade) {
      _setCurrentEntidade(authState.entidade);
      setIsLoadingEntidade(false);
    } else if (!loggedInUser) {
      // Limpa o estado quando o usuário faz logout
      _setCurrentEntidade(null);
      setIsLoadingEntidade(true);
    }
  }, [loggedInUser, authState.entidade]);

  // Efeito que busca a configuração da agenda QUANDO a entidade é definida
  useEffect(() => {
    const fetchAgendaConfig = async () => {
      // Condições para buscar:
      // 1. Usuário autenticado.
      // 2. Entidade definida.
      if (authState.isAuthenticated && currentEntidade?.id) {
        setIsLoadingAgendaConfig(true);
        try {
          // Este endpoint busca detalhes como feriados e dias bloqueados,
          // complementando os dados de horários que já vêm com a entidade no login.
          const response = await axios.get(`/api/entidades/${currentEntidade.id}/agenda-config`);
          setAgendaConfig(response.data || { feriados: [], diasBloqueados: [] });
        } catch (error) {
          console.error("Erro ao buscar configuração da agenda no EntidadeContext:", error);
          setAgendaConfig({ feriados: [], diasBloqueados: [] }); // Define um valor padrão em caso de erro
        } finally {
          setIsLoadingAgendaConfig(false);
        }
      } else if (!authState.isAuthenticated) {
        // Se o usuário deslogar, limpa a configuração
        setAgendaConfig({ feriados: [], diasBloqueados: [] });
        setIsLoadingAgendaConfig(true);
      }
    };

    fetchAgendaConfig();
  }, [authState.isAuthenticated, currentEntidade?.id]); // Depende do ID da entidade para refetch

  // Efeito para buscar todos os dados de suporte da entidade
  useEffect(() => {
    const fetchAllSupportData = async () => {
      if (authState.isAuthenticated && currentEntidade?.id) {
        setIsLoadingSupportData(true);
        try {
          const [especialidadesRes, medicosRes, servicosRes, pacientesRes, funcionariosRes] = await Promise.all([
            axios.get(`/api/entidades/${currentEntidade.id}/especialidades-corpo-clinico`),
            axios.get(`/api/entidades/${currentEntidade.id}/funcionarios-corpo-clinico`),
            axios.get(`/api/servicos?entidade_id=${currentEntidade.id}`),
            axios.get(`/api/entidades/${currentEntidade.id}/pacientes/basic-info`),
            axios.get(`/api/funcionarios?entidade_id=${currentEntidade.id}`)
          ]);

          setSupportData({
            especialidades: especialidadesRes.data.especialidades || [],
            todosMedicos: medicosRes.data.funcionarios || [],
            allFuncionarios: funcionariosRes.data.funcionarios || [],
            pacientesList: pacientesRes.data.pacientes || [],
            servicos: servicosRes.data.servicos || [],
          });
        } catch (error) {
          console.error("Erro ao buscar dados de suporte no EntidadeContext:", error);
        } finally {
          setIsLoadingSupportData(false);
        }
      }
    };
    fetchAllSupportData();
  }, [authState.isAuthenticated, currentEntidade?.id]); // Depende do ID da entidade para refetch

  const value = useMemo(() => ({
    currentEntidade,
    setCurrentEntidade,
    isLoadingEntidade,
    agendaConfig,
    isLoadingAgendaConfig,
    supportData, // Contém a pacientesList
    isLoadingSupportData,
    addPacienteToList // Expõe a nova função
  }), [
    currentEntidade, setCurrentEntidade, isLoadingEntidade,
    agendaConfig, isLoadingAgendaConfig,
    supportData, isLoadingSupportData,
    addPacienteToList // Adiciona ao array de dependências
  ]);

  return (
    <EntidadeContext.Provider value={value}>
      {children}
    </EntidadeContext.Provider>
  );
};

export const useEntidadeContext = () => {
  const context = useContext(EntidadeContext);
  if (context === null) {
    throw new Error('useEntidadeContext deve ser usado dentro de um EntidadeProvider');
  }
  return context;
};